import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FileText,
  Palette,
  Hammer,
  HardHat,
  Wrench,
  Ruler,
  PaintBucket,
  Drill,
  Layers,
  LayoutGrid,
  ShieldCheck,
  Upload,
  Gift,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

const OFICIOS = [
  { id: "reformas_integrales", label: "Reformas integrales" },
  { id: "multiservicios", label: "Multiservicios" },
  { id: "electricidad", label: "Electricidad" },
  { id: "fontaneria", label: "Fontanería" },
  { id: "pintura", label: "Pintura" },
  { id: "albanileria", label: "Albañilería" },
  { id: "climatizacion", label: "Climatización" },
  { id: "carpinteria", label: "Carpintería" },
  { id: "construccion", label: "Construcción" },
  { id: "cerrajeria", label: "Cerrajería" },
  { id: "impermeabilizacion", label: "Impermeabilización" },
  { id: "aislamientos", label: "Aislamientos" },
];

const COLORES = [
  { id: "azul_corporativo", nombre: "Azul corporativo", hex: "#2563EB" },
  { id: "negro_elegante", nombre: "Negro elegante", hex: "#1A1A1A" },
  { id: "gris_pizarra", nombre: "Gris pizarra", hex: "#475569" },
  { id: "verde_obra", nombre: "Verde obra", hex: "#15803D" },
  { id: "naranja_construccion", nombre: "Naranja construcción", hex: "#EA580C" },
  { id: "granate", nombre: "Granate", hex: "#9F1239" },
  { id: "marron_madera", nombre: "Marrón madera", hex: "#78350F" },
];

const ICONOS_PROVISIONALES = [
  { id: "hammer", Icono: Hammer, label: "Martillo" },
  { id: "hardhat", Icono: HardHat, label: "Casco" },
  { id: "wrench", Icono: Wrench, label: "Llave inglesa" },
  { id: "ruler", Icono: Ruler, label: "Regla" },
  { id: "paint", Icono: PaintBucket, label: "Pintura" },
  { id: "drill", Icono: Drill, label: "Taladro" },
  { id: "layers", Icono: Layers, label: "Capas" },
  { id: "grid", Icono: LayoutGrid, label: "Cuadrícula" },
  { id: "shield", Icono: ShieldCheck, label: "Escudo" },
];

function HeaderProgreso({ paso }) {
  const pct = Math.round((paso / 3) * 100);
  return (
    <div className="border-b border-slate-200 bg-white">
      <div className="max-w-3xl mx-auto px-6 pt-5 pb-3">
        <div className="flex items-center justify-between text-xs font-medium text-slate-600 mb-2">
          <span>Paso {paso} de 3</span>
          <span>{pct}%</span>
        </div>
        <div className="rx-progress-bar">
          <div style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function HeroIcono({ Icono }) {
  return (
    <div className="flex justify-center mb-4">
      <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 ring-1 ring-blue-100">
        <Icono className="w-6 h-6" />
      </div>
    </div>
  );
}

function Paso1({ inicial, onContinuar }) {
  const [nombreComercial, setNombreComercial] = useState(inicial?.nombreComercial || "");
  const [oficio, setOficio] = useState(inicial?.oficioPrincipal || "");
  const [oficioOtro, setOficioOtro] = useState(inicial?.oficioOtro || "");
  const [enviando, setEnviando] = useState(false);

  const valido = useMemo(() => {
    if (!nombreComercial.trim()) return false;
    if (!oficio) return false;
    if (oficio === "otro" && !oficioOtro.trim()) return false;
    return true;
  }, [nombreComercial, oficio, oficioOtro]);

  const submit = async (e) => {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    try {
      await onContinuar({
        nombreComercial: nombreComercial.trim(),
        oficioPrincipal: oficio,
        oficioOtro: oficio === "otro" ? oficioOtro.trim() : null,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="onboarding-paso1">
      <HeroIcono Icono={Building2} />
      <h1 className="text-2xl sm:text-3xl font-display font-bold text-center tracking-tight">
        ¡Vamos a preparar tu negocio!
      </h1>
      <p className="text-sm text-slate-500 text-center mt-2 mb-8">
        En menos de 2 minutos tendrás todo listo para crear presupuestos profesionales.
      </p>

      <div className="space-y-6">
        <div>
          <label className="text-sm font-semibold text-slate-800 block">
            Nombre comercial *
          </label>
          <p className="text-xs text-slate-500 mt-0.5 mb-2">
            Así te verán tus clientes en los presupuestos y comunicaciones.
          </p>
          <Input
            value={nombreComercial}
            onChange={(e) => setNombreComercial(e.target.value)}
            placeholder="Ej: Reformas García"
            className="h-11"
            data-testid="onb-nombre-comercial"
            autoFocus
          />
        </div>

        <div>
          <label className="text-sm font-semibold text-slate-800 block mb-2">
            Tu oficio principal *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {OFICIOS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOficio(o.id)}
                className={cn(
                  "text-left rounded-lg border bg-white px-4 py-3 text-sm transition-colors",
                  oficio === o.id
                    ? "border-blue-500 ring-2 ring-blue-100 text-blue-700 font-semibold"
                    : "border-slate-200 hover:border-slate-300 text-slate-700",
                )}
                data-testid={`onb-oficio-${o.id}`}
              >
                {o.label}
              </button>
            ))}
            <input
              type="text"
              value={oficio === "otro" ? oficioOtro : ""}
              onFocus={() => setOficio("otro")}
              onChange={(e) => {
                setOficio("otro");
                setOficioOtro(e.target.value);
              }}
              placeholder="Otro oficio…"
              className={cn(
                "rounded-lg border bg-white px-4 py-3 text-sm transition-colors col-span-1 sm:col-span-2",
                oficio === "otro"
                  ? "border-blue-500 ring-2 ring-blue-100 text-blue-700"
                  : "border-slate-200 hover:border-slate-300 text-slate-700",
              )}
              data-testid="onb-oficio-otro"
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Button
          type="submit"
          disabled={!valido || enviando}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white gap-2 disabled:bg-blue-300"
          data-testid="onb-paso1-continuar"
        >
          Continuar
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
}

function Paso2({ inicial, onAtras, onContinuar }) {
  const [razonSocial, setRazonSocial] = useState(inicial?.razonSocial || "");
  const [cifNif, setCifNif] = useState(inicial?.cifNif || "");
  const [telefono, setTelefono] = useState(inicial?.telefono || "");
  const [direccion, setDireccion] = useState(inicial?.direccion || "");
  const [ciudad, setCiudad] = useState(inicial?.ciudad || "");
  const [cp, setCp] = useState(inicial?.cp || "");
  const [enviando, setEnviando] = useState(false);

  const valido = telefono.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    try {
      await onContinuar({
        razonSocial: razonSocial.trim() || null,
        cifNif: cifNif.trim() || null,
        telefono: telefono.trim(),
        direccion: direccion.trim() || null,
        ciudad: ciudad.trim() || null,
        cp: cp.trim() || null,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="onboarding-paso2">
      <HeroIcono Icono={FileText} />
      <h1 className="text-2xl sm:text-3xl font-display font-bold text-center tracking-tight">
        Tus datos fiscales
      </h1>
      <div className="rx-info-banner mt-4 mb-6">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Estos datos aparecen en tus presupuestos. Solo el teléfono es obligatorio ahora — el resto puedes completarlo después en Configuración.
        </span>
      </div>

      <div className="space-y-5">
        <Campo label="Razón Social">
          <Input
            value={razonSocial}
            onChange={(e) => setRazonSocial(e.target.value)}
            className="h-11"
            data-testid="onb-razon-social"
          />
        </Campo>
        <Campo label="CIF o NIF de tu empresa">
          <Input
            value={cifNif}
            onChange={(e) => setCifNif(e.target.value)}
            className="h-11"
            data-testid="onb-cif"
          />
        </Campo>
        <Campo label="Teléfono *">
          <Input
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            inputMode="tel"
            className="h-11"
            data-testid="onb-telefono"
            required
          />
        </Campo>
        <Campo label="Dirección">
          <Input
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            className="h-11"
            data-testid="onb-direccion"
          />
        </Campo>
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Ciudad">
            <Input
              value={ciudad}
              onChange={(e) => setCiudad(e.target.value)}
              className="h-11"
              data-testid="onb-ciudad"
            />
          </Campo>
          <Campo label="C.P.">
            <Input
              value={cp}
              onChange={(e) => setCp(e.target.value)}
              className="h-11"
              data-testid="onb-cp"
            />
          </Campo>
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onAtras}
          className="h-12 px-5 gap-2 border-slate-300"
          data-testid="onb-paso2-atras"
        >
          <ArrowLeft className="w-4 h-4" /> Atrás
        </Button>
        <Button
          type="submit"
          disabled={!valido || enviando}
          className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white gap-2 disabled:bg-blue-300"
          data-testid="onb-paso2-continuar"
        >
          Continuar
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </form>
  );
}

function Campo({ label, children }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-800 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Paso3({ inicial, onAtras, onContinuar, onSaltar }) {
  const [colorId, setColorId] = useState(
    inicial?.colorCorporativo
      ? COLORES.find((c) => c.hex.toLowerCase() === String(inicial.colorCorporativo).toLowerCase())?.id || COLORES[0].id
      : COLORES[1].id,
  );
  const [logoUrl, setLogoUrl] = useState(inicial?.logoUrl || "");
  const [iconoId, setIconoId] = useState(inicial?.logoIcono || null);
  const [busquedaIconos, setBusquedaIconos] = useState("");
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef(null);

  const colorElegido = COLORES.find((c) => c.id === colorId) || COLORES[0];
  const iconosFiltrados = useMemo(() => {
    const q = busquedaIconos.trim().toLowerCase();
    if (!q) return ICONOS_PROVISIONALES;
    return ICONOS_PROVISIONALES.filter((i) => i.label.toLowerCase().includes(q));
  }, [busquedaIconos]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoUrl(String(reader.result || ""));
      setIconoId(null);
    };
    reader.readAsDataURL(f);
  };

  const enviar = async (saltar) => {
    if (enviando) return;
    setEnviando(true);
    try {
      await onContinuar(
        saltar
          ? { colorCorporativo: null, logoUrl: null, logoIcono: null }
          : {
              colorCorporativo: colorElegido.hex,
              logoUrl: logoUrl || null,
              logoIcono: logoUrl ? null : iconoId || null,
            },
        { saltar },
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div data-testid="onboarding-paso3">
      <HeroIcono Icono={Palette} />
      <h1 className="text-2xl sm:text-3xl font-display font-bold text-center tracking-tight">
        Tu logo y colores
      </h1>
      <p className="text-sm text-slate-500 text-center mt-2 mb-8">
        Dale personalidad a tus presupuestos. Puedes saltarte este paso y configurarlo después.
      </p>

      <div className="space-y-6">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Color corporativo</h2>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            Se aplica a las bandas de capítulo y la portada de tus PDFs.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {COLORES.map((c) => {
              const sel = c.id === colorId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColorId(c.id)}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all ring-offset-2",
                    sel ? "ring-2 ring-slate-900" : "",
                  )}
                  style={{ background: c.hex }}
                  aria-label={c.nombre}
                  data-testid={`onb-color-${c.id}`}
                >
                  {sel ? (
                    <span className="text-white text-sm font-bold">✓</span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-2 text-sm text-slate-500">
            <span>
              Seleccionado: <strong className="text-slate-800">{colorElegido.nombre}</strong>
            </span>
            <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
              {colorElegido.hex}
            </span>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold text-slate-800 mb-2">Sube tu logo</h2>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-300 rounded-xl py-10 flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            data-testid="onb-logo-drop"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="max-h-20 max-w-full object-contain"
              />
            ) : (
              <>
                <Upload className="w-5 h-5 text-slate-500" />
                <span className="text-sm text-slate-500">Pulsa para subir tu logo</span>
              </>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileChange}
              data-testid="onb-logo-file"
            />
          </button>
        </div>

        <div className="rx-card p-4">
          <h3 className="text-sm font-semibold text-slate-800">
            ¿No lo tienes a mano? Elige un icono provisional en 1 click
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">
            Se mostrará como símbolo en tus presupuestos PDF. Puedes cambiarlo cuando quieras.
          </p>
          <div className="grid grid-cols-7 gap-2">
            {iconosFiltrados.map(({ id, Icono }) => {
              const sel = iconoId === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setIconoId(id);
                    setLogoUrl("");
                  }}
                  className={cn(
                    "aspect-square rounded-lg border bg-white flex items-center justify-center transition-colors",
                    sel
                      ? "border-blue-500 ring-2 ring-blue-100 text-blue-600"
                      : "border-slate-200 hover:border-slate-300 text-slate-700",
                  )}
                  data-testid={`onb-icono-${id}`}
                  aria-label={`Icono ${id}`}
                >
                  <Icono className="w-5 h-5" />
                </button>
              );
            })}
          </div>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={busquedaIconos}
              onChange={(e) => setBusquedaIconos(e.target.value)}
              placeholder="Buscar más iconos…"
              className="pl-9 h-10 bg-slate-50"
              data-testid="onb-icono-buscar"
            />
          </div>
        </div>

        <div className="rounded-xl bg-blue-50/60 border border-blue-100 px-4 py-3 flex gap-3 items-start">
          <Gift className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-700">
            <strong>¿Quieres un logo personalizado?</strong> Si te suscribes a cualquier plan, te creamos un logo profesional gratis.
          </p>
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onAtras}
          className="h-12 px-5 gap-2 border-slate-300"
          data-testid="onb-paso3-atras"
        >
          <ArrowLeft className="w-4 h-4" /> Atrás
        </Button>
        <Button
          type="button"
          onClick={() => enviar(false)}
          disabled={enviando}
          className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 text-white gap-2"
          data-testid="onb-paso3-continuar"
        >
          Continuar
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="text-center mt-3">
        <button
          type="button"
          onClick={() => enviar(true)}
          disabled={enviando}
          className="text-sm text-slate-500 underline hover:text-slate-700"
          data-testid="onb-paso3-saltar"
        >
          Saltar este paso y configurarlo después
        </button>
      </div>
    </div>
  );
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { account, guardarPaso, completar } = useAuth();
  const onb = account?.onboarding || {};
  const [paso, setPaso] = useState(() => {
    if (onb.completado) return 4;
    return Math.min(Math.max(Number(onb.pasoActual || 1), 1), 3);
  });

  useEffect(() => {
    if (account?.onboarding?.completado) {
      navigate("/", { replace: true });
    }
  }, [account, navigate]);

  const onPaso1 = async (datos) => {
    try {
      await guardarPaso(1, datos);
      setPaso(2);
    } catch (err) {
      console.error("onb_paso1_error", err);
      toast.error("No se pudo guardar el paso 1");
    }
  };

  const onPaso2 = async (datos) => {
    try {
      await guardarPaso(2, datos);
      setPaso(3);
    } catch (err) {
      console.error("onb_paso2_error", err);
      toast.error("No se pudo guardar el paso 2");
    }
  };

  const onPaso3 = async (datos, { saltar } = {}) => {
    try {
      if (!saltar) {
        await guardarPaso(3, datos);
      }
      await completar();
      toast.success("Configuración inicial completada");
      navigate("/", { replace: true });
    } catch (err) {
      console.error("onb_paso3_error", err);
      toast.error("No se pudo completar el onboarding");
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50">
      <HeaderProgreso paso={paso} />
      <div className="max-w-2xl mx-auto px-6 py-10">
        {paso === 1 ? (
          <Paso1 inicial={onb.paso1} onContinuar={onPaso1} />
        ) : paso === 2 ? (
          <Paso2 inicial={onb.paso2} onAtras={() => setPaso(1)} onContinuar={onPaso2} />
        ) : (
          <Paso3
            inicial={onb.paso3}
            onAtras={() => setPaso(2)}
            onContinuar={onPaso3}
            onSaltar={() => onPaso3(null, { saltar: true })}
          />
        )}
      </div>
    </div>
  );
}
